<template>
  <Transition enter-active-class="transition-opacity duration-300 ease-out" enter-from-class="opacity-0" leave-active-class="transition-opacity duration-300 ease-in" leave-to-class="opacity-0">
    <div v-if="isOpen && !isSideLayout" class="fixed inset-0 z-inpage-modal" @click="handleClose" />
  </Transition>
  <Transition
    enter-active-class="transition-transform duration-300 ease-out"
    leave-active-class="transition-transform duration-300 ease-out"
    :enter-from-class="isDesktop ? 'translate-x-full' : 'translate-y-full'"
    :leave-to-class="isDesktop ? 'translate-x-full' : 'translate-y-full'"
  >
    <div
      v-if="isOpen"
      :class="[
        'fixed flex flex-col bg-white',
        isSideLayout ? 'right-0 top-0 z-header h-dvh w-[400px] border-l border-gray-200' : 'z-inpage-modal shadow-lg',
        isDesktop && !isSideLayout ? 'right-0 top-0 h-dvh w-[400px]' : '',
        !isDesktop ? 'inset-0' : '',
      ]"
    >
      <ChatHeader @close="handleClose" @reset="handleReset" />
      <template v-if="!hasConsented">
        <ChatTermsAgreement :slug="slug" @agree="handleAcceptTerms" />
      </template>
      <template v-else>
        <ChatMessageList :disable-select-tags="isRateLimited" :is-loading="isLoading" :messages="messages as ChatMessage[]" @click-navigation="handleClickNavigation" @select-tag="handleSelectTag" />
        <ChatInput :disabled="isRateLimited" @submit="handleSubmit" />
        <ChatDisclaimer />
      </template>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { captureException } from '@/wabunka-src/common/utils/sentry';
import ChatDisclaimer from '@/wabunka-src/features/Chat/components/model/pure/ChatDisclaimer/ChatDisclaimer.vue';
import ChatHeader from '@/wabunka-src/features/Chat/components/model/pure/ChatHeader/ChatHeader.vue';
import ChatInput from '@/wabunka-src/features/Chat/components/model/pure/ChatInput/ChatInput.vue';
import ChatMessageList from '@/wabunka-src/features/Chat/components/model/pure/ChatMessageList/ChatMessageList.vue';
import ChatTermsAgreement from '@/wabunka-src/features/Chat/components/model/pure/ChatTermsAgreement/ChatTermsAgreement.vue';
import { useChatConsent } from '@/wabunka-src/features/Chat/composables/general/useChatConsent';
import { useChatMessages } from '@/wabunka-src/features/Chat/composables/general/useChatMessages';
import { useChatPanel } from '@/wabunka-src/features/Chat/composables/general/useChatPanel';
import { useChatSession } from '@/wabunka-src/features/Chat/composables/general/useChatSession';
import type { ChatMessage } from '@/wabunka-src/features/Chat/models/chatMessage';
import {
  sendGAAiChatAcceptTermsEvent,
  sendGAAiChatClickNavigationEvent,
  sendGAAiChatCloseEvent,
  sendGAAiChatErrorEvent,
  sendGAAiChatReceiveResponseEvent,
  sendGAAiChatSelectTagEvent,
  sendGAAiChatSendMessageEvent,
} from '@/wabunka-src/features/Chat/utils/googleAnalytics/chatEvents';

interface Props {
  slug: string;
  onClickBookingNavigation?: () => void;
}

const props = defineProps<Props>();

const { isOpen, isDesktop, isSideLayout, close } = useChatPanel();
const { hasConsented, acceptTerms } = useChatConsent();
const { sessionId } = useChatSession();
const { messages, isLoading, isRateLimited, sendMessage, resetMessages } = useChatMessages();

/** 利用規約同意 */
const handleAcceptTerms = () => {
  sendGAAiChatAcceptTermsEvent(props.slug);
  acceptTerms();
};

/** メッセージ送信 */
const handleSubmit = async (text: string) => {
  sendGAAiChatSendMessageEvent(props.slug, text.length);
  const startTime = Date.now();

  await sendMessage(text, sessionId.value, props.slug);

  // 最新メッセージで結果を判定
  const lastMessage = messages.value[messages.value.length - 1];
  if (lastMessage?.role === 'assistant') {
    const responseTimeMs = Date.now() - startTime;
    const hasError = lastMessage.blocks.some((b) => b.type === 'error');

    if (hasError) {
      sendGAAiChatErrorEvent(props.slug, undefined);
      captureException(new Error(`AI Chat error response for slug: ${props.slug}`), 'wabunka');
    } else {
      sendGAAiChatReceiveResponseEvent(props.slug, 'in', responseTimeMs);
    }
  }
};

/** SelectTag クリック → メッセージとして送信 */
const handleSelectTag = (tag: string) => {
  sendGAAiChatSelectTagEvent(props.slug, tag);
  handleSubmit(tag);
};

/** ナビゲーションクリック */
const handleClickNavigation = (payload: { navType: string; url?: string; label?: string }) => {
  sendGAAiChatClickNavigationEvent(props.slug, payload.navType);
  if (payload.navType === 'booking' || payload.url === '/reserve') {
    props.onClickBookingNavigation?.();
  }
};

/** パネルを閉じる */
const handleClose = () => {
  sendGAAiChatCloseEvent(props.slug, messages.value.length);
  close();
};

/** 会話リセット（sessionIdは維持 — レートリミットキーとして使用されるため） */
const handleReset = () => {
  resetMessages();
};
</script>
